<template>
    <div class="AdminAudit">
        <WCard>
            <div class="Header flex row-stretch-center">
                <div>
                    <h2 class="Title">
                        {{ $t('audit.title') }}
                    </h2>
                    <p class="Subtitle">
                        {{ $t('audit.subtitle') }}
                    </p>
                </div>
                <div
                    v-if="statsSummary"
                    class="Stats"
                >
                    <span class="Stats__period">{{ $t('audit.stats.title') }}</span>
                    <span class="Stats__item"><strong>{{ statsSummary.total }}</strong> {{ $t('audit.stats.totalExports') }}</span>
                    <span class="Stats__item"><strong>{{ statsSummary.users }}</strong> {{ $t('audit.stats.distinctUsers') }}</span>
                    <span
                        v-if="statsSummary.top"
                        class="Stats__item"
                    >{{ $t('audit.stats.topUser') }}: <strong>{{ statsSummary.top.userEmail }}</strong> ({{ statsSummary.top.count }})</span>
                </div>
            </div>

            <div class="Filters flex row-stretch-center">
                <div class="flex row-center flex-wrap">
                    <div class="FormCell">
                        <WSelect
                            name="userId"
                            :value="currentFilters.userId"
                            :options="userOptions"
                            :placeholder="$t('audit.filters.user')"
                            @input="handleFilterInput('userId', $event)"
                        />
                    </div>
                    <div class="FormCell">
                        <WSelect
                            name="entity"
                            :value="currentFilters.entity"
                            :options="entityOptions"
                            :placeholder="$t('audit.filters.entity')"
                            @input="handleFilterInput('entity', $event)"
                        />
                    </div>
                    <div class="FormCell">
                        <WSelect
                            name="format"
                            :value="currentFilters.format"
                            :options="formatOptions"
                            :placeholder="$t('audit.filters.format')"
                            @input="handleFilterInput('format', $event)"
                        />
                    </div>
                    <div class="FormCell">
                        <WSelect
                            name="status"
                            :value="currentFilters.status"
                            :options="statusOptions"
                            :placeholder="$t('audit.filters.status')"
                            @input="handleFilterInput('status', $event)"
                        />
                    </div>
                    <div class="FormCell FormCell--date">
                        <WDatepicker
                            name="date"
                            :value="currentFilters.date"
                            :placeholder="$t('audit.filters.date')"
                            mode="range"
                            @input="handleFilterInput('date', $event)"
                        />
                    </div>
                </div>
                <div class="flex row-center">
                    <WButton
                        outline
                        @click="handleResetFilters"
                    >
                        {{ $t('general.reset') }}
                    </WButton>
                    <WButton
                        color="primary"
                        class="ml-1"
                        @click="onSubmitFilters"
                    >
                        {{ $t('general.apply') }}
                    </WButton>
                </div>
            </div>

            <DataGrid
                ref="dataGrid"
                class="DataGrid"
                v-bind="dataGridConfig"
                :columns="columns"
                :rows="rows"
                :loading="isFetching"
                :contextMenu="contextMenu"
            />

            <div
                v-if="lastPage > 1"
                class="Pagination flex row-center"
            >
                <WPaginate
                    :selected="pagination.page"
                    :pages="lastPage"
                    @onChange="handlePagination"
                />
            </div>
        </WCard>
    </div>
</template>

<script>
import {
    Api, apiRequest, rethrowError, notifyError,
} from '@/api'
import { DataGrid } from '@/components'
import { userMixin } from '@/mixins'
import { ENTITIES } from '@/entities/entities'
import Grid from './grid'

const DEFAULT_FILTERS = {
    userId: '',
    entity: '',
    format: '',
    status: '',
    date: undefined,
}

const PER_PAGE = 50

export default {
    name: 'AdminAudit',
    components: {
        DataGrid,
    },
    mixins: [userMixin],
    metaInfo () {
        return {
            title: this.$t('audit.title'),
        }
    },
    data () {
        return {
            columns: Grid.columns,
            dataGridConfig: { ...Grid.config },
            rows: [],
            total: 0,
            isFetching: false,
            stats: null,

            userOptions: [],
            entityOptions: ENTITIES.map(entity => ({ key: entity.path, label: entity.label })),
            formatOptions: [
                { key: 'csv', label: 'CSV' },
                { key: 'excel', label: 'Excel' },
            ],
            statusOptions: [
                { key: 'success', label: this.$t('audit.status.success') },
                { key: 'failed', label: this.$t('audit.status.failed') },
                { key: 'partial', label: this.$t('audit.status.partial') },
            ],

            currentFilters: { ...DEFAULT_FILTERS },
            pagination: { page: 1, perPage: PER_PAGE },
        }
    },
    computed: {
        lastPage () {
            return Math.max(1, Math.ceil((this.total || 0) / this.pagination.perPage))
        },
        statsSummary () {
            if (!this.stats) return null
            const users = this.stats.exportsByUser || []
            return {
                total: this.stats.totalExports || 0,
                users: users.length,
                top: users[0] || null,
            }
        },
    },
    watch: {
        // Guard: this view exposes per-user download activity, so it is admin-only.
        // The backend also enforces ADMIN (RolesGuard); this redirect is the UI guard.
        // Waits until the user is loaded to avoid redirecting an admin on a hard reload.
        me: {
            immediate: true,
            handler (value) {
                if (!value) return
                if (this.isAdmin) this.loadAll()
                else this.$router.replace({ name: 'AdminDashboard' })
            },
        },
    },
    methods: {
        contextMenu () {
            // Do not offer a client-side export of the audit log itself (it would be unaudited).
            return ['copy', 'copyWithHeaders']
        },
        loadAll () {
            this.fetchUsers()
            this.fetchStats()
            this.fetchAudits()
        },
        buildParams () {
            const {
                userId, entity, format, status, date,
            } = this.currentFilters
            const { page, perPage } = this.pagination
            return {
                ...(userId ? { userId } : {}),
                ...(entity ? { entity } : {}),
                ...(format ? { format } : {}),
                ...(status ? { status } : {}),
                ...(date && date.start ? { fromDate: date.start, toDate: date.end } : {}),
                limit: perPage,
                offset: (page - 1) * perPage,
            }
        },
        fetchAudits () {
            this.isFetching = true
            apiRequest(Api().audit.exports(this.buildParams()))
                .then(({ records, total }) => {
                    this.rows = (records || []).map(this.normalizeRecord)
                    this.total = total || 0
                })
                .catch(rethrowError(notifyError()))
                .finally(() => (this.isFetching = false))
        },
        fetchUsers () {
            apiRequest(Api().admin.usersList())
                .then(({ data }) => {
                    this.userOptions = (data || []).map(user => ({
                        key: user.id,
                        label: user.name || user.email,
                    }))
                })
                .catch(rethrowError(notifyError()))
        },
        fetchStats () {
            apiRequest(Api().audit.stats({ days: 30 }))
                .then((stats) => { this.stats = stats })
                .catch(() => { this.stats = null })
        },
        normalizeRecord (record) {
            const user = record.user || {}
            return {
                ...record,
                userName: user.name || '',
                userEmail: user.email || '',
                entityLabel: this.entityLabel(record.entity),
                formatLabel: this.formatLabel(record.format),
                statusLabel: this.statusLabel(record.status),
                recordsLabel: record.recordCount != null ? String(record.recordCount) : '—',
                sizeLabel: this.formatBytes(record.fileSizeBytes),
            }
        },
        entityLabel (entity) {
            const key = `admin.navigation.entities.${entity}`
            return this.$te(key) ? this.$t(key) : (entity || '')
        },
        formatLabel (format) {
            if (format === 'excel') return 'Excel'
            if (format === 'csv') return 'CSV'
            return format || ''
        },
        statusLabel (status) {
            const key = `audit.status.${status}`
            return this.$te(key) ? this.$t(key) : (status || '')
        },
        formatBytes (bytes) {
            if (bytes === null || bytes === undefined || bytes === '') return '—'
            const value = Number(bytes)
            if (Number.isNaN(value)) return '—'
            if (value === 0) return '0 B'
            const units = ['B', 'KB', 'MB', 'GB', 'TB']
            const exp = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1)
            const size = value / (1024 ** exp)
            return `${exp === 0 ? size : size.toFixed(1)} ${units[exp]}`
        },
        handleFilterInput (name, value) {
            this.currentFilters = { ...this.currentFilters, [name]: value }
        },
        onSubmitFilters () {
            this.pagination = { ...this.pagination, page: 1 }
            this.fetchAudits()
        },
        handleResetFilters () {
            this.currentFilters = { ...DEFAULT_FILTERS }
            this.pagination = { ...this.pagination, page: 1 }
            this.fetchAudits()
        },
        handlePagination (page) {
            this.pagination = { ...this.pagination, page }
            this.fetchAudits()
        },
    },
}
</script>

<style lang="scss" scoped>
.AdminAudit {
    .Header {
        margin-bottom: 16px;
    }

    .Title {
        margin: 0;
        font-size: 18px;
        font-weight: 600;
    }

    .Subtitle {
        margin: 2px 0 0;
        font-size: 13px;
        opacity: 0.6;
    }

    .Stats {
        display: flex;
        align-items: center;
        font-size: 13px;
        opacity: 0.8;

        &__period {
            font-weight: 600;
        }

        &__item {
            white-space: nowrap;
        }

        &__item::before {
            content: '·';
            margin: 0 10px;
            opacity: 0.5;
        }
    }

    .Filters {
        margin-bottom: 16px;
    }

    .FormCell {
        width: 165px;
        margin-bottom: 8px;

        & + .FormCell {
            margin-left: 12px;
        }

        .WDatepicker::v-deep .Input {
            margin-bottom: 0;
        }
    }

    .DataGrid {
        height: calc(100vh - 300px);
        min-height: 360px;
    }

    .Pagination {
        margin-top: 16px;
    }
}
</style>
